<template>
  <div
    class="flex align-items-center justify-content-center overflow-hidden w-100 p-1"
    :style="{ color: getFolderOrFileTypeColor(file?.mimeType) }"
    :class="{ hoverAndShadowEffectVisible }"
  >
    <div v-if="file?.hasThumbnail || file?.mimeType.startsWith('image/')" class="preview-container">
      <div class="image-container">
        <img
          v-if="previewUrl != null"
          :src="previewUrl"
          :style="`max-width: ${sizeX}; max-height: ${sizeY}`"
          :class="{ 'recently-change': recentlyChanged, 'preview-image-shadow': true }"
        />
      </div>
      <div v-if="showOverlay" class="overlay">
        <i
          :class="getFolderFileIcon(file?.mimeType)"
          :style="`font-size: calc(min(${sizeX == 'unset' ? '10em' : sizeX}, ${sizeY == 'unset' ? '10em' : sizeY}) - 2em)`"
        />
      </div>
    </div>
    <div v-else-if="!hideWithoutThumbnail" class="flex justify-content-center">
      <i
        :class="{
          [getFolderFileIcon(file?.mimeType)]: true,
          'recently-change': recentlyChanged,
        }"
        :style="`font-size: min(${sizeX == 'unset' ? '10em' : sizeX}, ${sizeY == 'unset' ? '10em' : sizeY})`"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { getFolderFileIcon } from "../helper/FMFilePreviewHelper";
import { getFolderOrFileTypeColor } from "../helper/FMFolderHelper";
import { useMinioSignedUrl } from "@/functions/useMinioSignedUrl";

const props = defineProps({
  file: {
    type: Object as PropType<{
      mimeType: string;
      fileUUID?: string;
      hasThumbnail: boolean;
    }>,
    required: false,
    default: undefined,
  },
  hideWithoutThumbnail: {
    type: Boolean,
    required: false,
    default: true,
  },
  sizeX: {
    type: String,
    required: false,
    default: "10em",
  },
  sizeY: {
    type: String,
    required: false,
    default: "10em",
  },
  hoverAndShadowEffectVisible: {
    type: Boolean,
    required: false,
    default: false,
  },
  containerFixedHeight: {
    type: Boolean,
    required: false,
    default: true,
  },
  showOverlay: {
    type: Boolean,
    required: false,
    default: false,
  },
  recentlyChanged: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const cssHeight = computed(() => {
  return props.containerFixedHeight ? props.sizeY : "auto";
});

const previewUUID = computed(() => {
  if (props.file?.fileUUID == null) {
    return undefined;
  }

  return `${props.file.fileUUID}${props.file.hasThumbnail ? "_thumbnail" : ""}`;
});
const { url: previewUrl } = useMinioSignedUrl(previewUUID);
</script>

<style lang="scss" scoped>
@use "@/modules/filemanagement/components/FMFileViewer/FMFileViewer.scss" as *;
.preview-container {
  position: relative;
  width: v-bind(sizeX);
  max-height: v-bind(sizeY);
  height: v-bind(cssHeight);

  .image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .overlay {
    position: absolute;
    inset: 0;
    transform: scale(0.3);
    opacity: 0.8;
    background-color: var(--p-gray-100);
    box-shadow: 0 0 5px 3px var(--p-gray-100);
    border-radius: var(--p-border-radius-md);
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.hoverAndShadowEffectVisible {
  transition: transform 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
  }
}
</style>
